class FreeShippingGoal extends HTMLElement {
  constructor() {
    super();
    // Normaliser et sécuriser l'amount provenant du data-attribute (attendu en centimes)
    const raw = (this.dataset.goalAmount ?? '0').toString();
    const normalized = raw.replace(/[^0-9.,-]/g, '').replace(',', '.');
    const cents = Number(normalized);
    this.goalAmount = Number.isFinite(cents) ? cents / 100 : 0; // Convertir de centimes vers la devise locale
    this.onCartUpdate = this.observeTotal.bind(this);
    this.currencyCode = this.dataset.currency || (window.Shopify && Shopify.currency && Shopify.currency.active) || null;
    this.locale = document.documentElement.lang || navigator.language || 'en';
    this.currencyPrefix = '';
    this.currencySuffix = '';
    this.decimalSeparator = null;
  }

  connectedCallback() {
    this.progress = this.querySelector('progress-bar');
    this.leftToSpend = this.querySelector('[data-left-to-spend]');
    this.observeTotal();
    document.addEventListener('cart:updated', this.onCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:updated', this.onCartUpdate);
    if (this.observer) this.observer.disconnect();
  }

  observeTotal() {
    const container = this.closest('.cart, .cart-drawer') || document;
    const target = container.querySelector('.totals__subtotal-value');

    if (!target) return;

    if (this.observer) this.observer.disconnect();

    // Detect currency formatting from the subtotal element
    this.detectCurrencyFromText(target.textContent || '');
    this.updateShippingGoal(this.parsePrice(target.textContent));
    this.observer = new MutationObserver(() => {
      this.detectCurrencyFromText(target.textContent || '');
      this.updateShippingGoal(this.parsePrice(target.textContent));
    });

    this.observer.observe(target, {
      characterData: true,
      childList: true,
      subtree: true,
    });
  }

  parsePrice(text) {
    let number = (text || '').replace(/[^0-9,\.\-]/g, "").replace(/\s/g, "");
    const lastComma = number.lastIndexOf(",");
    const lastDot = number.lastIndexOf(".");

    if (lastComma > lastDot) {
      number = number.replace(/\./g, "").replace(",", ".");
    } else {
      number = number.replace(/,/g, "");
    }

    const val = parseFloat(number);
    return Number.isFinite(val) ? val : 0;
  }

  detectCurrencyFromText(text) {
    const trimmed = (text || '').trim();
    if (!trimmed) return;

    // Match a contiguous numeric portion (with spaces/commas/dots inside)
    const m = trimmed.match(/[0-9][0-9\s.,]*[0-9]/);
    if (!m || typeof m.index !== 'number') return;

    const start = m.index;
    const end = start + m[0].length - 1;
    this.currencyPrefix = trimmed.slice(0, start);
    this.currencySuffix = trimmed.slice(end + 1);

    // Extract decimal separator from the numeric portion
    const numericPortion = m[0];
    const lastComma = numericPortion.lastIndexOf(',');
    const lastDot = numericPortion.lastIndexOf('.');
    this.decimalSeparator = lastComma > lastDot ? ',' : (lastDot > -1 ? '.' : null);
  }

  formatAmount(amount) {
    // Prefer Intl with currency code when available
    try {
      if (this.currencyCode) {
        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currencyCode,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(amount);
      }
    } catch (e) {
      // Fallback below
    }

    // Fallback: use detected prefix/suffix and detected decimal separator
    let value = (Number(amount) || 0).toFixed(2);
    if (this.decimalSeparator && this.decimalSeparator !== '.') {
      value = value.replace('.', this.decimalSeparator);
    }
    return `${this.currencyPrefix || ''}${value}${this.currencySuffix || ''}`;
  }

  updateShippingGoal(cartTotal) {
    // Garde-fous contre NaN/Infinity et objectif invalide
    if (!Number.isFinite(cartTotal) || !Number.isFinite(this.goalAmount) || this.goalAmount <= 0) {
      if (this.progress) this.progress.style.setProperty('--percent', '0%');
      if (this.leftToSpend && Number.isFinite(cartTotal) && Number.isFinite(this.goalAmount) && this.goalAmount > 0) {
        const remaining = Math.max(this.goalAmount - cartTotal, 0);
        this.leftToSpend.innerHTML = this.formatAmount(remaining);
      }
      return;
    }

    if (cartTotal >= this.goalAmount) {
      // Objectif atteint
      this.classList.add('free-shipping-goal--done');
      this.progress.style.setProperty('--percent', '100%');
    } else {
      // Objectif non atteint
      this.classList.remove('free-shipping-goal--done');
      const remaining = this.goalAmount - cartTotal;
      const percent = Math.max(0, Math.min(100, (cartTotal * 100) / this.goalAmount));
      
      this.progress.style.setProperty('--percent', `${percent}%`);
      if (this.leftToSpend) {
        this.leftToSpend.innerHTML = this.formatAmount(remaining);
      }
    }
  }
}

if (!customElements.get("free-shipping-goal")) {
  customElements.define("free-shipping-goal", FreeShippingGoal);
}
